import {
  WORK_ORDER_CATEGORIES,
  WORK_ORDER_CATEGORY_LABELS,
  WORK_ORDER_SEVERITIES,
  WORK_ORDER_SEVERITY_LABELS,
  type CreateWorkOrderInput,
  type WorkOrderCategory,
  type WorkOrderSeverity,
} from '@sitewise/shared';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import { Camera, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { listBuildings, listUnits } from '../../../api/buildings.js';
import { listTeams } from '../../../api/teams.js';
import { presignUpload as legacyPresign } from '../../../api/uploads.js';
import {
  createWorkOrder,
  type WorkOrderSummary,
} from '../../../api/work-orders.js';
import { VoiceRecorder } from '../../../components/voice-recorder.js';
import { ApiError } from '../../../lib/api-client.js';
import { queueDraftWorkOrder } from '../../../offline/inspection-runner.js';
import { deletePhoto, savePhoto } from '../../../offline/photos.js';
import { useOfflineContext } from '../../../offline/use-offline-context.js';
import { useSyncStore } from '../../../stores/sync-store.js';

const LOCATION_AUTO_SELECT_THRESHOLD = 0.55;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional pre-fill — used by the building detail page. */
  buildingId?: string;
  unitId?: string | null;
  onCreated?: (wo: WorkOrderSummary) => void;
}

interface FormState {
  buildingId: string;
  unitId: string;
  title: string;
  description: string;
  category: WorkOrderCategory;
  severity: WorkOrderSeverity;
  dueAt: string;
  assignedTeamId: string;
  remindBeforeDays: '' | '1' | '2' | '3';
  /** Pest WOs only: units to seed `extermination_logs` rows for. */
  unitLogUnitIds: string[];
}

interface PendingPhoto {
  id: string;
  uploadId?: string;
  localPhotoId?: string;
  file: File;
  previewUrl: string;
  state: 'pending' | 'uploading' | 'uploaded' | 'error';
  error?: string;
}

const EMPTY: FormState = {
  buildingId: '',
  unitId: '',
  title: '',
  description: '',
  category: 'other',
  severity: 'medium',
  dueAt: '',
  assignedTeamId: '',
  remindBeforeDays: '',
  unitLogUnitIds: [],
};

export function CreateWorkOrderSheet({
  open,
  onOpenChange,
  buildingId: initialBuildingId,
  unitId: initialUnitId,
  onCreated,
}: Props) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const buildingId = useId();
  const unitId = useId();
  const descId = useId();
  const catId = useId();
  const sevId = useId();
  const dueId = useId();
  const teamId = useId();
  const remindId = useId();

  const [form, setForm] = useState<FormState>({
    ...EMPTY,
    buildingId: initialBuildingId ?? '',
    unitId: initialUnitId ?? '',
  });
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [voiceMemoUploadId, setVoiceMemoUploadId] = useState<string | null>(null);
  const [voiceMemoTranscript, setVoiceMemoTranscript] = useState<string | null>(null);
  const [voiceLocationHint, setVoiceLocationHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        buildingId: initialBuildingId ?? '',
        unitId: initialUnitId ?? '',
      });
      setPhotos([]);
      setVoiceMemoUploadId(null);
      setVoiceMemoTranscript(null);
      setVoiceLocationHint(null);
      setError(null);
    } else {
      // Revoke object URLs to avoid leaking memory between opens.
      photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBuildingId, initialUnitId]);

  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'wo-create'],
    queryFn: () => listBuildings({ limit: 100 }),
    enabled: open,
  });

  const unitsQuery = useQuery({
    queryKey: ['units', form.buildingId],
    queryFn: () => listUnits(form.buildingId),
    enabled: open && Boolean(form.buildingId),
  });

  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: listTeams,
    enabled: open,
  });

  const offlineCtx = useOfflineContext();
  const isOnline = useSyncStore((s) => s.isOnline);
  const createMutation = useMutation({
    mutationFn: async (input: CreateWorkOrderInput) => {
      if (!isOnline && offlineCtx.userId && offlineCtx.orgSlug) {
        const offlinePhotoIds = photos
          .map((photo) => photo.localPhotoId)
          .filter((id): id is string => typeof id === 'string' && id.length > 0);
        const queued = await queueDraftWorkOrder({
          userId: offlineCtx.userId,
          orgSlug: offlineCtx.orgSlug,
          buildingId: input.buildingId,
          unitId: input.unitId ?? null,
          title: input.title,
          description: input.description ?? null,
          category: input.category,
          priority:
            'severity' in input && typeof (input as { severity?: string }).severity === 'string'
              ? ((input as { severity?: string }).severity ?? null)
              : null,
          photoIds: offlinePhotoIds,
        });
        return {
          queued: true,
          workOrder: {
            displayNumber: queued.localId,
          } as unknown as WorkOrderSummary,
        };
      }
      const result = await createWorkOrder(input);
      return { queued: false, ...result };
    },
    onSuccess: (data) => {
      if ('queued' in data && data.queued) {
        toast.success('Work order queued — will sync when reconnected');
      } else {
        toast.success(`Work order ${data.workOrder.displayNumber} created`);
      }
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['work-order-stats'] });
      onCreated?.(data.workOrder);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create work order');
    },
  });

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    const list = Array.from(files);
    for (const file of list) {
      if (!file.type.startsWith('image/')) continue;
      const id = `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
      const previewUrl = URL.createObjectURL(file);
      if (!isOnline && offlineCtx.userId && offlineCtx.orgSlug) {
        const localPhotoId = await savePhoto({
          userId: offlineCtx.userId,
          orgSlug: offlineCtx.orgSlug,
          blob: file,
          filename: file.name,
          parentKey: `work_order:new:${Date.now()}`,
        });
        setPhotos((prev) => [
          ...prev,
          { id, file, previewUrl, state: 'uploaded', localPhotoId },
        ]);
        continue;
      }
      setPhotos((prev) => [...prev, { id, file, previewUrl, state: 'pending' }]);
      // Background upload — compress, presign, PUT.
      void uploadPhoto(id, file);
    }
  }

  async function uploadPhoto(id: string, file: File) {
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, state: 'uploading' } : p)),
    );
    try {
      // Phase 2 verification target: a 5 MB phone photo should arrive at
      // Spaces under ~500 KB. 0.5 MB ceiling + 2048 px max-dim hits that
      // budget on real-world shots without sacrificing legibility for
      // triage. The library falls back to PNG/JPEG quality steps until
      // the size constraint is satisfied.
      // Force JPEG output: phone cameras (esp. iOS) hand us HEIC/HEIF, which
      // isn't an allowed `work_order_photo` content type and would 400 at
      // presign. Re-encoding to JPEG guarantees an accepted type.
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });
      const jpegName = file.name.replace(/\.[^.]+$/, '') || 'photo';
      const presigned = await legacyPresign({
        kind: 'work_order_photo',
        filename: `${jpegName}.jpg`,
        contentType: 'image/jpeg',
        sizeBytes: compressed.size,
      });
      await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.headers,
        body: compressed,
      });
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, state: 'uploaded', uploadId: presigned.uploadId } : p,
        ),
      );
    } catch (err) {
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                state: 'error',
                error: err instanceof Error ? err.message : 'Upload failed',
              }
            : p,
        ),
      );
    }
  }

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found) {
        URL.revokeObjectURL(found.previewUrl);
        if (found.localPhotoId) {
          void deletePhoto(found.localPhotoId);
        }
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.buildingId) {
      setError('Building is required');
      return;
    }
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (photos.some((p) => p.state === 'uploading' || p.state === 'pending')) {
      setError('Wait for photos to finish uploading');
      return;
    }
    const uploadIds = photos
      .filter((p) => p.state === 'uploaded' && p.uploadId)
      .map((p) => p.uploadId!);

    setBusy(true);
    try {
      const isPestUnitVisit = form.category === 'pest' && form.unitLogUnitIds.length > 0;
      const remindBeforeDays =
        form.remindBeforeDays === '1' || form.remindBeforeDays === '2' || form.remindBeforeDays === '3'
          ? (Number(form.remindBeforeDays) as 1 | 2 | 3)
          : null;
      await createMutation.mutateAsync({
        buildingId: form.buildingId,
        unitId: form.unitId || null,
        title: form.title.trim(),
        description: form.description.trim() || null,
        category: form.category,
        severity: form.severity,
        assignedTeamId: form.assignedTeamId || null,
        assignedUserId: null,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
        remindBeforeDays: form.dueAt ? remindBeforeDays : null,
        attachmentUploadIds: uploadIds,
        ...(voiceMemoUploadId
          ? {
              voiceMemo: {
                uploadId: voiceMemoUploadId,
                ...(voiceMemoTranscript ? { transcript: voiceMemoTranscript } : {}),
              },
            }
          : {}),
        unitLogUnitIds: isPestUnitVisit ? form.unitLogUnitIds : undefined,
        metadata: isPestUnitVisit ? { kind: 'unit_visit_log' } : {},
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full max-w-xl flex-col overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New work order</SheetTitle>
          <SheetDescription>
            Submit a maintenance task. Add photos to make triage faster.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-4 flex flex-1 flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={buildingId}>Building</Label>
            <select
              id={buildingId}
              required
              className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
              value={form.buildingId}
              onChange={(e) => setForm({ ...form, buildingId: e.target.value, unitId: '' })}
              disabled={Boolean(initialBuildingId)}
            >
              <option value="">Select a building</option>
              {buildingsQuery.data?.buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} — {b.addressLine1}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={unitId}>Unit (optional)</Label>
            <select
              id={unitId}
              className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
              value={form.unitId}
              onChange={(e) => setForm({ ...form, unitId: e.target.value })}
              disabled={!form.buildingId}
            >
              <option value="">No specific unit</option>
              {unitsQuery.data?.units.map((u) => (
                <option key={u.id} value={u.id}>
                  Unit {u.unitNumber}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5 rounded border border-dashed border-border p-3">
            <Label className="text-xs uppercase tracking-wide text-fg-muted">
              Voice memo (optional)
            </Label>
            <p className="text-xs text-fg-muted">
              Speak the issue — including building name, address, or apartment when you know them —
              and SiteWise will fill in the title, description, severity, category, building, and unit.
              The audio is also saved as an attachment.
            </p>
            <VoiceRecorder
              {...(form.buildingId ? { buildingId: form.buildingId } : {})}
              onReset={() => {
                setVoiceMemoUploadId(null);
                setVoiceMemoTranscript(null);
                setVoiceLocationHint(null);
              }}
              onComplete={(result) => {
                setVoiceMemoUploadId(result.uploadId);
                setVoiceMemoTranscript(result.transcript || null);
                if (result.draft) {
                  const suggestedBuildingId = result.draft.buildingId ?? null;
                  const suggestedUnitId = result.draft.unitId ?? null;
                  // Use locationConfidence only — never fall back to title/category
                  // confidence. Missing locationConfidence with a sanitized ID means
                  // the model matched but omitted the score; treat as auto-applicable.
                  const locationConfidence =
                    typeof result.draft.locationConfidence === 'number'
                      ? result.draft.locationConfidence
                      : null;
                  const locationConfidentEnough =
                    locationConfidence === null
                      ? Boolean(suggestedBuildingId || suggestedUnitId)
                      : locationConfidence >= LOCATION_AUTO_SELECT_THRESHOLD;

                  const lockedBuildingId = initialBuildingId ?? null;
                  const canApplyBuilding =
                    Boolean(suggestedBuildingId) &&
                    locationConfidentEnough &&
                    (!lockedBuildingId || lockedBuildingId === suggestedBuildingId);

                  if (
                    suggestedBuildingId &&
                    !canApplyBuilding &&
                    !lockedBuildingId &&
                    locationConfidence !== null &&
                    locationConfidence < LOCATION_AUTO_SELECT_THRESHOLD
                  ) {
                    const match = buildingsQuery.data?.buildings.find(
                      (b) => b.id === suggestedBuildingId,
                    );
                    setVoiceLocationHint(
                      match
                        ? `Suggested building: ${match.name} (${Math.round(locationConfidence * 100)}% confidence) — select it above if correct.`
                        : 'Voice suggested a building; pick it from the dropdown to confirm.',
                    );
                  } else {
                    setVoiceLocationHint(null);
                  }

                  setForm((prev) => {
                    const nextBuildingId = canApplyBuilding
                      ? suggestedBuildingId!
                      : prev.buildingId;
                    const effectiveBuildingId =
                      nextBuildingId || lockedBuildingId || prev.buildingId || '';
                    const canApplyUnit =
                      Boolean(suggestedUnitId) &&
                      locationConfidentEnough &&
                      Boolean(effectiveBuildingId) &&
                      // When a different building was suggested but we can't apply it
                      // (locked), only apply the unit if it was meant for that building.
                      (!suggestedBuildingId ||
                        suggestedBuildingId === effectiveBuildingId);

                    return {
                      ...prev,
                      title: result.draft?.title ?? prev.title,
                      description: result.draft?.description ?? prev.description,
                      severity:
                        (result.draft?.severity as WorkOrderSeverity | undefined) ??
                        prev.severity,
                      category:
                        WORK_ORDER_CATEGORIES.includes(
                          result.draft?.category as WorkOrderCategory,
                        )
                          ? (result.draft!.category as WorkOrderCategory)
                          : prev.category,
                      ...(canApplyBuilding ? { buildingId: nextBuildingId } : {}),
                      ...(canApplyUnit ? { unitId: suggestedUnitId! } : {}),
                    };
                  });
                } else if (result.transcript) {
                  setForm((prev) => ({
                    ...prev,
                    description:
                      prev.description.trim().length > 0
                        ? prev.description
                        : result.transcript,
                  }));
                }
              }}
            />
            {voiceLocationHint ? (
              <p className="text-xs text-fg-secondary">{voiceLocationHint}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={titleId}>Title</Label>
            <Input
              id={titleId}
              required
              maxLength={200}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Leaking pipe under kitchen sink"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={descId}>Description</Label>
            <textarea
              id={descId}
              maxLength={8000}
              rows={4}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Add detail to help the responder triage."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={catId}>Category</Label>
              <select
                id={catId}
                className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value as WorkOrderCategory })
                }
              >
                {WORK_ORDER_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {WORK_ORDER_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={sevId}>Severity</Label>
              <select
                id={sevId}
                className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
                value={form.severity}
                onChange={(e) =>
                  setForm({ ...form, severity: e.target.value as WorkOrderSeverity })
                }
              >
                {WORK_ORDER_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {WORK_ORDER_SEVERITY_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={teamId}>Assign team (optional)</Label>
            <select
              id={teamId}
              className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
              value={form.assignedTeamId}
              onChange={(e) => setForm({ ...form, assignedTeamId: e.target.value })}
            >
              <option value="">Unassigned</option>
              {(teamsQuery.data?.teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={dueId}>Due date</Label>
              <Input
                id={dueId}
                type="date"
                value={form.dueAt}
                onChange={(e) =>
                  setForm({
                    ...form,
                    dueAt: e.target.value,
                    remindBeforeDays: e.target.value ? form.remindBeforeDays : '',
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={remindId}>Remind me</Label>
              <select
                id={remindId}
                className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
                value={form.remindBeforeDays}
                onChange={(e) =>
                  setForm({
                    ...form,
                    remindBeforeDays: e.target.value as FormState['remindBeforeDays'],
                  })
                }
                disabled={!form.dueAt}
              >
                <option value="">No reminder</option>
                <option value="1">1 day before</option>
                <option value="2">2 days before</option>
                <option value="3">3 days before</option>
              </select>
            </div>
          </div>

          {form.category === 'pest' && form.buildingId ? (
            <div className="flex flex-col gap-1.5">
              <Label>Per-unit visit (extermination logs)</Label>
              <p className="text-xs text-fg-muted">
                Select units this visit covers. The vendor will see a per-unit checklist instead of a
                single sign-off.
              </p>
              <UnitMultiSelect
                units={unitsQuery.data?.units ?? []}
                value={form.unitLogUnitIds}
                onChange={(ids) => setForm({ ...form, unitLogUnitIds: ids })}
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label>Photos</Label>
            <div className="flex flex-wrap gap-2">
              {photos.map((p) => (
                <div
                  key={p.id}
                  className="relative h-20 w-20 overflow-hidden rounded-md border border-border"
                >
                  <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                    aria-label="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  {p.state === 'uploading' || p.state === 'pending' ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-xs text-white">
                      …
                    </div>
                  ) : null}
                  {p.state === 'error' ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-danger/70 text-[10px] text-white">
                      Failed
                    </div>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border text-fg-secondary hover:bg-surface-muted"
              >
                <Camera className="h-5 w-5" />
                <span className="text-[10px]">Add photo</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                className="sr-only"
                onChange={(e) => {
                  void handleFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>
          </div>

          {error ? (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          ) : null}

          <SheetFooter className="mt-auto">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create work order'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function UnitMultiSelect({
  units,
  value,
  onChange,
}: {
  units: Array<{ id: string; unitNumber: string }>;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const allSelected = units.length > 0 && value.length === units.length;
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs">
        <span className="text-fg-muted">
          {value.length} of {units.length} selected
        </span>
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={() => onChange(allSelected ? [] : units.map((u) => u.id))}
        >
          {allSelected ? 'Clear' : 'Select all'}
        </button>
      </div>
      <ul className="max-h-48 overflow-y-auto p-2 text-sm">
        {units.map((u) => (
          <li key={u.id}>
            <label className="flex items-center gap-2 rounded px-2 py-1 hover:bg-surface-muted">
              <input
                type="checkbox"
                checked={value.includes(u.id)}
                onChange={() => toggle(u.id)}
              />
              <span>Unit {u.unitNumber}</span>
            </label>
          </li>
        ))}
        {units.length === 0 ? (
          <li className="px-2 py-1 text-xs text-fg-muted">No units found for this building.</li>
        ) : null}
      </ul>
    </div>
  );
}
